import { type ParentComponent, Show, createEffect, on } from 'solid-js';
import { useLocation, useNavigate } from '@solidjs/router';
import { Sidebar } from './Sidebar';
import { hasAdminSurface } from '~/shared/access/sections';
import { useAuth } from '~/shared/api/auth-context';
import { authApi } from '~/shared/api/auth-api';
import { NotForYou } from './NotForYou';
import { SectionGate } from './NoAccessPage';
import { deniedSection, firstOpenSection, sectionOf } from './section-access';
import { clearForbidden, forbiddenAt } from '~/shared/api/forbidden';
import { TopBar } from './TopBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useDefaultPaletteCommands } from '~/widgets/shell/CommandPalette';
import { SettingsSpotlight } from '~/widgets/settings/SettingsSpotlight';
import { SettingsNavPanel } from '~/widgets/settings/SettingsNavPanel';
import { TournamentPanelProvider } from '~/widgets/tournaments/TournamentPanelProvider';
import { ChatPanelProvider } from '~/features/chat/ChatPanelProvider';

export const AppShell: ParentComponent = (props) => {
  useDefaultPaletteCommands();
  const location = useLocation();
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  const roles = () => session()?.user.roles ?? [];
  const permissions = () => session()?.user.permissions ?? [];

  createEffect(on(() => location.pathname, (path) => {
    if (forbiddenAt() !== null && forbiddenAt() !== path) clearForbidden();
  }, { defer: true }));
  const locked = () => !hasAdminSurface(session()?.user.permissions ?? []);

  const signOutHere = async () => {
    await authApi.logout().catch(() => undefined);
    signOut();
    navigate('/login');
  };

  return (
    <Show when={!locked()} fallback={<NotForYou roles={roles()} onSignOut={() => void signOutHere()} />}>
    <TournamentPanelProvider>
      <ChatPanelProvider>
      <div class="min-h-screen flex flex-col">
        <ImpersonationBanner />
        <div class="flex flex-1 min-h-0">
        <Sidebar />
        <Show
          when={location.pathname.startsWith('/settings')
            && deniedSection(location.pathname, permissions()) === null
            && forbiddenAt() !== location.pathname}
        >
          <SettingsNavPanel />
        </Show>
        <div class="flex-1 min-w-0 flex flex-col">
          <TopBar />
          <main class="flex-1 mx-4 my-4">
            <SectionGate
              pathname={location.pathname}
              denied={deniedSection(location.pathname, permissions())}
              forbiddenAt={forbiddenAt()}
              section={sectionOf(location.pathname)}
              onHome={() => navigate(firstOpenSection(permissions()))}
            >
              {props.children}
            </SectionGate>
          </main>
        </div>
        <SettingsSpotlight />
        </div>
      </div>
      </ChatPanelProvider>
    </TournamentPanelProvider>
    </Show>
  );
};
